// client/src/components/common/UserAvatar.jsx
import React, { useMemo } from 'react';
import { Avatar, Box, Tooltip } from '@mui/material';
import { useSelector } from 'react-redux';
import { getAvatarSrc } from '../../utils/avatar';

// ============================================================================
// Пресеты размеров
// ============================================================================

const SIZE_MAP = {
  xs: 20,
  sm: 24,
  md: 32,
  lg: 48,
  xl: 64,
  xxl: 96,
  hero: 128,
};

function resolveSize(size) {
  if (typeof size === 'number') return size;
  return SIZE_MAP[size] || SIZE_MAP.md;
}

// ============================================================================
// Компонент
// ============================================================================

/**
 * Универсальный аватар пользователя.
 *
 * Источник данных (в порядке приоритета):
 *   1. Явно переданный `access` (объект с avatar_base64 / kratos_identity_id)
 *   2. `state.auth.access` (если access не передан)
 *
 * Если у пользователя нет своего аватара — DiceBear identicon (по kratos_id).
 *
 * @param {object} props
 * @param {object} [props.access] — опционально, иначе берётся из Redux
 * @param {'xs'|'sm'|'md'|'lg'|'xl'|'xxl'|'hero'|number} [props.size='md']
 * @param {string} [props.tooltip] — текст тултипа (если не указан, не показывается)
 * @param {boolean} [props.showBorder=false] — рамка вокруг аватара
 * @param {object} [props.sx] — MUI sx для тонкой настройки
 * @param {string} [props.alt] — alt-текст
 */
export default function UserAvatar({
  access,
  size = 'md',
  tooltip,
  showBorder = false,
  sx,
  alt,
}) {
  const reduxAccess = useSelector((state) => state.auth?.access);
  const finalAccess = access !== undefined ? access : reduxAccess;

  const pxSize = resolveSize(size);

  const src = useMemo(() => getAvatarSrc(finalAccess), [finalAccess]);

  const finalAlt =
    alt ||
    finalAccess?.display_name ||
    finalAccess?.email ||
    'User';

  const avatar = (
    <Avatar
      src={src}
      alt={finalAlt}
      sx={{
        width: pxSize,
        height: pxSize,
        fontSize: Math.max(10, Math.round(pxSize * 0.4)),
        border: showBorder ? '2px solid' : 'none',
        borderColor: showBorder ? 'divider' : 'transparent',
        bgcolor: 'grey.300',
        ...sx,
      }}
    >
      {(finalAlt || '?').charAt(0).toUpperCase()}
    </Avatar>
  );

  if (!tooltip) return avatar;

  return (
    <Tooltip title={tooltip} arrow>
      <Box sx={{ display: 'inline-flex' }}>{avatar}</Box>
    </Tooltip>
  );
}

// ============================================================================
// Экспорт пресетов (для удобства)
// ============================================================================

export { SIZE_MAP };
